import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import FirmaCanvas from '../components/FirmaCanvas';
import FotoCaptura from '../components/FotoCaptura';
import checklists, { SECCIONES } from '../data/checklists';
import api from '../utils/api';
import { agregarACola } from '../hooks/useOfflineQueue';
import { getDraft, saveDraft, deleteDraft } from '../hooks/useDrafts';
import { useAuth } from '../hooks/useAuth';
import { generarClientFormId } from '../utils/idempotency';
import { hoyChile } from '../utils/fecha';

const ESTADOS = ['OK', 'RO', 'RAI', 'NA'];

// Clasificación de observaciones RO (ambas obligatorias). El color refleja la prioridad.
const PRIORIDADES = [
  { v: 'alta',  label: 'Alta',  color: '#E5533D' },
  { v: 'media', label: 'Media', color: '#E5A93D' },
  { v: 'baja',  label: 'Baja',  color: '#38BDF8' },
];
const CATEGORIAS = [
  { v: 'critica',   label: 'Crítica funcionamiento' },
  { v: 'normativa', label: 'Normativa' },
];

export default function FormMantenimiento() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tecnico } = useAuth();
  const [paso, setPaso] = useState('qr');
  const [ascensor, setAscensor] = useState(null);
  const [folio, setFolio] = useState('');
  const [error, setError] = useState('');

  const [fecha] = useState(hoyChile());
  const [nombreCliente, setNombreCliente] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [estadoEquipo, setEstadoEquipo] = useState('Operativo');
  const [firmaTecnico, setFirmaTecnico] = useState(null);
  const [firmaCliente, setFirmaCliente] = useState(null);
  const [infoInterna, setInfoInterna] = useState('');
  const [mostrarInfoInterna, setMostrarInfoInterna] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [items, setItems] = useState({});
  const [ofertaBorrador, setOfertaBorrador] = useState(null);

  const mes = new Date().getMonth() + 1;
  const checklistMes = checklists[mes] || checklists[1];

  const initItems = () => {
    const init = {};
    checklistMes.forEach((item, i) => {
      init[item.key] = { estado: null, observacion: '', orden: i };
    });
    setItems(init);
  };

  const handleQRResult = (asc) => {
    setAscensor(asc);
    initItems();
    setPaso('form');
  };

  // Navegación desde modal de borradores en Home: salta el paso QR
  useEffect(() => {
    if (location.state?.ascensor) {
      setAscensor(location.state.ascensor);
      initItems();
      setPaso('form');
    }
  }, []);

  useEffect(() => {
    if (!ascensor || !tecnico?.id) return;
    const draft = getDraft(tecnico.id, 'mantenimiento', ascensor.id);
    if (!draft) return;
    setOfertaBorrador(draft);
  }, [ascensor?.id, tecnico?.id]);

  useEffect(() => {
    if (paso !== 'enviando') return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [paso]);

  const restaurarDraft = (draft) => {
    if (draft.checklist) setItems(draft.checklist);
    if (draft.nombreCliente !== undefined) setNombreCliente(draft.nombreCliente);
    if (draft.observaciones !== undefined) setObservaciones(draft.observaciones);
    if (draft.estadoEquipo !== undefined) setEstadoEquipo(draft.estadoEquipo);
    if (draft.infoInterna !== undefined) setInfoInterna(draft.infoInterna);
    if (draft.mostrarInfoInterna !== undefined) setMostrarInfoInterna(draft.mostrarInfoInterna);
    if (draft.fotos) setFotos(draft.fotos);
    if (draft.firmaTecnico) setFirmaTecnico(draft.firmaTecnico);
    if (draft.firmaCliente) setFirmaCliente(draft.firmaCliente);
  };

  const guardarBorrador = () => {
    try {
      saveDraft(tecnico.id, {
        tipo: 'mantenimiento',
        ascensor_id: ascensor.id,
        ascensor,
        checklist: items,
        nombreCliente,
        observaciones,
        estadoEquipo,
        infoInterna,
        mostrarInfoInterna,
        fotos,
        firmaTecnico,
        firmaCliente,
      });
      alert('Borrador guardado. Puedes retomarlo en las próximas 24 horas.');
    } catch (e) {
      alert(e.message);
    }
  };

  const setEstado = (key, estado) => {
    setItems(prev => ({ ...prev, [key]: { ...prev[key], estado } }));
  };

  const setObsItem = (key, obs) => {
    setItems(prev => ({ ...prev, [key]: { ...prev[key], observacion: obs } }));
  };

  const setPrioridad = (key, prioridad) => {
    setItems(prev => ({ ...prev, [key]: { ...prev[key], prioridad } }));
  };

  const setCategoria = (key, categoria) => {
    setItems(prev => ({ ...prev, [key]: { ...prev[key], categoria } }));
  };

  const validar = () => {
    for (const item of checklistMes) {
      const it = items[item.key];
      if (it?.estado === 'RAI' && !it?.observacion?.trim()) {
        return `El ítem "${item.descripcion}" está marcado como RAI — debes escribir una observación.`;
      }
      if (it?.estado === 'RO') {
        if (!it?.prioridad) return `El ítem "${item.descripcion}" (RO) requiere que indiques la prioridad.`;
        if (!it?.categoria) return `El ítem "${item.descripcion}" (RO) requiere que indiques la categoría (crítica o normativa).`;
        if (!it?.observacion?.trim()) return `El ítem "${item.descripcion}" (RO) requiere una observación que detalle el trabajo.`;
      }
    }
    if (!firmaTecnico) return 'La firma del técnico es obligatoria.';
    if (!firmaCliente) return 'La firma del cliente es obligatoria.';
    return null;
  };

  const completados = checklistMes.filter(i => items[i.key]?.estado).length;
  const porcentaje = Math.round((completados / checklistMes.length) * 100);
  const tieneRAI = checklistMes.some(i => items[i.key]?.estado === 'RAI');

  const handleEnviar = async () => {
    const errVal = validar();
    if (errVal) { setError(errVal); window.scrollTo(0, document.body.scrollHeight); return; }
    if (completados < checklistMes.length) {
      setError('Debes completar todos los ítems del checklist antes de enviar.');
      window.scrollTo(0, document.body.scrollHeight);
      return;
    }
    setError('');
    setPaso('enviando');

    const checklistPayload = checklistMes.map((item, i) => {
      const it = items[item.key] || {};
      const esRO = it.estado === 'RO';
      return {
        seccion: item.seccion,
        item_key: item.key,
        descripcion: item.descripcion,
        estado: it.estado,
        observacion: it.observacion || null,
        prioridad: esRO ? (it.prioridad || null) : null,
        categoria: esRO ? (it.categoria || null) : null,
        orden: i,
      };
    });

    const payload = {
      tipo: 'mantenimiento',
      client_form_id: generarClientFormId(),
      ascensor_id: ascensor.id,
      fecha_visita: fecha,
      nombre_cliente: nombreCliente,
      observaciones_generales: observaciones,
      firma_tecnico: firmaTecnico,
      firma_cliente: firmaCliente,
      info_interna: infoInterna || null,
      estado_final_equipo: estadoEquipo,
      fotos: fotos.map((f, i) => ({ dataURL: f.dataURL, nombre: f.nombre, orden: i })),
      checklist: checklistPayload,
    };

    try {
      const res = await api.post('/formularios', payload);
      deleteDraft(tecnico.id, 'mantenimiento', ascensor.id);
      setFolio(res.data.folio);
      setPaso('listo');
    } catch (err) {
      const esRed = !navigator.onLine || err.code === 'ERR_NETWORK' || !err.response;
      if (esRed) {
        const resultado = await agregarACola({
          tipo: 'mantenimiento',
          payload,
          ascensor: { comunidad: ascensor.comunidad, n_ascensor: ascensor.n_ascensor, torre: ascensor.torre },
        });
        if (resultado === 'ok') {
          deleteDraft(tecnico.id, 'mantenimiento', ascensor.id);
          setPaso('offline');
        } else if (resultado === 'cola_llena') {
          setError('La cola offline está llena (máx. 5 formularios). Envía los pendientes antes de continuar.');
          setPaso('form');
        } else {
          setError('Ya existe un formulario pendiente para este ascensor y fecha.');
          setPaso('form');
        }
      } else {
        setError(err.response?.data?.error || 'Error al enviar. Intenta de nuevo.');
        setPaso('form');
      }
    }
  };

  const FormSection = ({ title }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      marginTop: 28, marginBottom: 16,
    }}>
      <div style={{ height: 1, flex: 1, background: '#1E2922' }} />
      <span style={{
        fontSize: 11, fontWeight: 700, color: '#4A6058',
        textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap',
      }}>{title}</span>
      <div style={{ height: 1, flex: 1, background: '#1E2922' }} />
    </div>
  );

  const nombreMes = new Date(fecha + 'T12:00:00').toLocaleString('es-CL', { month: 'long' });

  const secciones = {};
  checklistMes.forEach(item => {
    if (!secciones[item.seccion]) secciones[item.seccion] = [];
    secciones[item.seccion].push(item);
  });

  if (paso === 'qr') return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <h1>Mantenimiento preventivo</h1>
      </div>
      <div className="page-content">
        <QRScanner onResult={handleQRResult} onCancel={() => navigate('/')} />
      </div>
    </div>
  );

  if (paso === 'enviando') return (
    <div className="app-container">
      <div className="app-header"><h1>Enviando...</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 48 }}>
        <div className="spinner" style={{ width: 48, height: 48, margin: '0 auto' }} />
        <p style={{ color: '#F4F7F5', fontWeight: 700, fontSize: 18, marginTop: 24, marginBottom: 8 }}>
          Enviando comprobante
        </p>
        <p style={{ color: '#8A9994', fontSize: 14, marginBottom: 24 }}>
          {ascensor?.comunidad} — Asc. {ascensor?.n_ascensor}
        </p>
        <div style={{
          background: '#1C1200', border: '1.5px solid #F59E0B',
          borderRadius: 12, padding: '16px 20px', maxWidth: 320, margin: '0 auto',
        }}>
          <p style={{ margin: 0, color: '#F59E0B', fontWeight: 700, fontSize: 15 }}>
            ⚠ No cierres esta ventana
          </p>
          <p style={{ margin: '8px 0 0', color: '#C9A84C', fontSize: 13, lineHeight: 1.5 }}>
            El formulario se está subiendo al servidor. Si cierras o minimizas la app antes de que termine, podrías perder el envío.
          </p>
        </div>
      </div>
    </div>
  );

  if (paso === 'offline') return (
    <div className="app-container">
      <div className="app-header"><h1>Guardado sin conexión</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>📦</div>
        <h2 style={{ marginBottom: 8 }}>Formulario guardado</h2>
        <p style={{ color: '#777', fontSize: 14, marginBottom: 8 }}>
          {ascensor.comunidad} — Ascensor {ascensor.n_ascensor} Torre {ascensor.torre}
        </p>
        <div style={{ background: '#1A1200', border: '1px solid #3D2B00', borderRadius: 10,
                      padding: '14px 16px', marginBottom: 28, textAlign: 'left' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#F59E0B', fontWeight: 600 }}>
            Sin conexión a internet
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#9FAAA5' }}>
            El formulario quedó guardado localmente. Se enviará automáticamente en cuanto recuperes la conexión.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/pendientes')}
          style={{ marginBottom: 10 }}>
          Ver comprobantes pendientes
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    </div>
  );

  if (paso === 'listo') return (
    <div className="app-container">
      <div className="app-header"><h1>¡Enviado!</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ marginBottom: 8 }}>Formulario completado</h2>
        <p style={{ color: '#9FAAA5', marginBottom: 8 }}>Folio: <strong style={{ color: '#F4F7F5' }}>{folio}</strong></p>
        <p style={{ color: '#9FAAA5', fontSize: 14, marginBottom: 32 }}>
          {ascensor.comunidad} — Ascensor {ascensor.n_ascensor} Torre {ascensor.torre}
        </p>
        {tieneRAI && (
          <div className="rai-banner" style={{ marginBottom: 24 }}>
            ⚠ Se notificó al administrador por ítems RAI
          </div>
        )}
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => setPaso('qr')}>←</button>
        <h1>Mantenimiento — {nombreMes}</h1>
      </div>
      <div className="page-content">

        <div className="ascensor-card">
          <div className="asc-title">{ascensor.comunidad}</div>
          <div className="asc-detail">📍 {ascensor.direccion}, {ascensor.comuna}</div>
          <div className="asc-detail">🛗 Ascensor N° {ascensor.n_ascensor} — Torre {ascensor.torre}</div>
          <div className="asc-detail">📅 {fecha}</div>
        </div>

        {ofertaBorrador && (
          <div style={{ background: '#1C1200', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#F59E0B', fontSize: 14 }}>
              Tienes un borrador guardado hace {Math.round((Date.now() - ofertaBorrador.saved_at) / 60000)} min
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { restaurarDraft(ofertaBorrador); setOfertaBorrador(null); }} style={{ background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}>Retomar</button>
              <button onClick={() => { deleteDraft(tecnico.id, 'mantenimiento', ascensor.id); setOfertaBorrador(null); }} style={{ background: 'transparent', color: '#F59E0B', border: '1px solid #F59E0B', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Ignorar</button>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        fontSize: 13, color: '#9FAAA5', marginBottom: 4 }}>
            <span>{completados} de {checklistMes.length} ítems</span>
            <span>{porcentaje}%</span>
          </div>
          <div className="progress-wrap">
            <div className="progress-bar" style={{ width: `${porcentaje}%` }} />
          </div>
        </div>

        {tieneRAI && (
          <div className="rai-banner">
            ⚠ Hay ítems con RAI — escribe una observación para cada uno
          </div>
        )}

        {Object.entries(secciones).map(([sec, secItems]) => (
          <div key={sec}>
            <div className="seccion-header">{SECCIONES[sec]}</div>
            {secItems.map(item => {
              const estado = items[item.key]?.estado;
              const obs = items[item.key]?.observacion || '';
              const prio = items[item.key]?.prioridad;
              const cat  = items[item.key]?.categoria;
              const esRAI = estado === 'RAI';
              const esRO  = estado === 'RO';
              const prioColor = PRIORIDADES.find(p => p.v === prio)?.color;
              return (
                <div key={item.key} className={`checklist-item ${esRAI ? 'has-RAI' : ''}`}
                  style={esRO && prioColor ? { borderLeft: `4px solid ${prioColor}` } : undefined}>
                  <div className="item-desc">{item.descripcion}</div>
                  <div className="estado-row">
                    {ESTADOS.map(e => (
                      <button key={e}
                        className={`estado-btn ${e} ${estado === e ? 'selected' : ''}`}
                        onClick={() => setEstado(item.key, e)}>{e}
                      </button>
                    ))}
                  </div>

                  {esRO && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 4 }}>Prioridad *</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {PRIORIDADES.map(p => (
                            <button key={p.v} type="button" onClick={() => setPrioridad(item.key, p.v)}
                              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                                       cursor: 'pointer', border: `1.5px solid ${p.color}`,
                                       background: prio === p.v ? p.color : 'transparent',
                                       color: prio === p.v ? '#0D1311' : p.color }}>
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 4 }}>Categoría *</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {CATEGORIAS.map(c => (
                            <button key={c.v} type="button" onClick={() => setCategoria(item.key, c.v)}
                              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                       cursor: 'pointer', border: '1.5px solid #2E4A40',
                                       background: cat === c.v ? '#0f6e56' : 'transparent',
                                       color: cat === c.v ? 'white' : '#C9D3CE' }}>
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {(esRAI || esRO) && (
                    <>
                      <input
                        className={`obs-input ${(esRAI || esRO) && !obs.trim() ? 'required' : ''}`}
                        placeholder={(esRAI || esRO) ? 'Observación obligatoria — detalla el trabajo...' : 'Observación (opcional)...'}
                        value={obs}
                        onChange={e => setObsItem(item.key, e.target.value)}
                      />
                      {(esRAI || esRO) && !obs.trim() && (
                        <div className="obs-required-msg">⚠ Debes describir el problema/trabajo</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <FormSection title="Cierre" />
        <div className="form-group">
          <label>Estado del equipo</label>
          <select value={estadoEquipo} onChange={e => setEstadoEquipo(e.target.value)}>
            <option value="Operativo">Operativo</option>
            <option value="Operativo en observación">Operativo en observación</option>
            <option value="Fuera de servicio">Fuera de servicio</option>
          </select>
        </div>
        <div className="form-group">
          <label>Nombre del cliente / representante</label>
          <input value={nombreCliente} onChange={e => setNombreCliente(e.target.value)}
            placeholder="Nombre de quien recibe" />
        </div>
        <div className="form-group">
          <label>Observaciones generales</label>
          <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)}
            placeholder="Notas adicionales sobre la visita..." />
        </div>

        <FormSection title="Evidencia fotográfica" />
        <FotoCaptura maxFotos={9} onChange={setFotos} />

        <div style={{ marginTop: 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10,
                          cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#8A9994' }}>
            <input
              type="checkbox"
              checked={mostrarInfoInterna}
              onChange={e => setMostrarInfoInterna(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#0f6e56' }}
            />
            Agregar nota interna (solo supervisores)
          </label>
          {mostrarInfoInterna && (
            <textarea
              value={infoInterna}
              onChange={e => setInfoInterna(e.target.value)}
              placeholder="Información interna para supervisores — no aparece en el comprobante del cliente..."
              style={{ width: '100%', marginTop: 10, padding: '10px 12px',
                       border: '1.5px solid #e65100', borderRadius: 10,
                       fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                       minHeight: 80, background: '#1A0D00', color: '#F4F7F5' }}
            />
          )}
        </div>

        <FormSection title="Firmas" />
        <FirmaCanvas label="Firma del técnico *" onChange={setFirmaTecnico} />
        <FirmaCanvas label="Firma del cliente (opcional)" onChange={setFirmaCliente} />

        {error && (
          <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 10,
                        padding: '12px 14px', fontSize: 13, marginBottom: 16,
                        border: '1px solid #3D1515' }}>
            {error}
          </div>
        )}

        <button type="button" onClick={guardarBorrador} style={{ background: '#1C1200', color: '#F59E0B', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 15, width: '100%', marginBottom: 10 }}>
          Guardar borrador
        </button>

        <button className="btn btn-primary" onClick={handleEnviar} style={{ marginBottom: 32 }}>
          Finalizar y enviar
        </button>
      </div>
    </div>
  );
}
